import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { NewsletterSection } from "@/components/NewsletterForm";
import { PageIntro, FadeUp } from "@/components/Reveal";
import { PatchBadge } from "@/components/Doodles";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink placeholder:text-ink/40";
const labelCls = "block text-sm font-semibold text-ink";

const InquiryForm = ({ kind, title, fields, testid, endpoint = "/submissions" }) => {
    const [form, setForm] = useState({});
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = endpoint === "/volunteers" ? form : { kind, ...form };
            const { data } = await api.post(endpoint, payload);
            toast.success(data.message || "Got it!");
            setDone(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    if (done) {
        return (
            <p data-testid={`${testid}-done`} className="mt-4 border-2 border-dashed border-forest bg-cream px-4 py-3 font-hand text-2xl text-forest -rotate-1">
                Received. A real human will read this and holler back.
            </p>
        );
    }

    return (
        <form onSubmit={submit} data-testid={`${testid}-form`} className="mt-4 space-y-4">
            <h3 className="font-display text-2xl tracking-wide text-ink">{title}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <label className={labelCls} htmlFor={`${testid}-name`}>Name</label>
                    <input id={`${testid}-name`} data-testid={`${testid}-name-input`} required className={inputCls}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                    <label className={labelCls} htmlFor={`${testid}-email`}>Email</label>
                    <input id={`${testid}-email`} data-testid={`${testid}-email-input`} type="email" required className={inputCls}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
            </div>
            {fields.map((f) => (
                <div key={f.key}>
                    <label className={labelCls} htmlFor={`${testid}-${f.key}`}>{f.label}</label>
                    {f.long ? (
                        <textarea id={`${testid}-${f.key}`} data-testid={`${testid}-${f.key}-input`} rows={3} placeholder={f.placeholder || ""}
                            className={inputCls} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                    ) : (
                        <input id={`${testid}-${f.key}`} data-testid={`${testid}-${f.key}-input`} placeholder={f.placeholder || ""}
                            className={inputCls} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                    )}
                </div>
            ))}
            <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="btn-stamp !bg-orange disabled:opacity-60">
                {busy ? "Sending…" : "Send It"}
            </button>
        </form>
    );
};

const PathwayCard = ({ kicker, title, body, children, rotate = "-rotate-1", testid }) => (
    <FadeUp>
        <div data-testid={testid} className={`relative h-full border-2 border-ink bg-paper p-7 hard-shadow-lg ${rotate} sm:p-9`}>
            <span className="tape-strip" aria-hidden="true" />
            <p className="font-hand text-xl text-brick">{kicker}</p>
            <h2 className="mt-1 font-display text-3xl tracking-wide text-ink">{title}</h2>
            <p className="mt-3 leading-relaxed text-ink/75">{body}</p>
            {children}
        </div>
    </FadeUp>
);

const GetInvolved = () => {
    const { t } = useContent();
    const ideas = t("host.ideas", []);
    const [openForm, setOpenForm] = useState(null);
    const toggle = (k) => setOpenForm((v) => (v === k ? null : k));

    return (
        <>
            <Seo
                title="Get Involved"
                description="Volunteer, host an event, collaborate, or partner with Panther City Clubhouse — a community clubhouse in Richland Hills, TX. Bring your big weird idea."
            />
            <PageIntro kicker="bring your big weird idea" title="GET INVOLVED" intro={t("involved.intro", "The Clubhouse runs on other people's enthusiasm. Volunteering, hosting, partnering, chipping in — pick a lane, or invent one.")} />

            <section className="px-5 pb-16 sm:px-10">
                <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2">
                    <PathwayCard testid="pathway-volunteer" kicker="hands on" title="VOLUNTEER" rotate="-rotate-[0.5deg]"
                        body={t("involved.volunteer_body", "Paint brushes, event setup, corkboard curation, herding folding chairs — the Clubhouse is built by volunteers, and there's always something to do.")}>
                        <button onClick={() => toggle("volunteer")} aria-expanded={openForm === "volunteer"} data-testid="volunteer-open-button" className="btn-stamp mt-5">
                            Sign Up to Volunteer
                        </button>
                        {openForm === "volunteer" && (
                            <InquiryForm endpoint="/volunteers" title="RAISE YOUR HAND" testid="volunteer"
                                fields={[
                                    { key: "skills", label: "Skills, superpowers, or strong opinions", placeholder: "carpentry, kava, chaos coordination…" },
                                    { key: "availability", label: "Availability", placeholder: "weeknights, weekends, whenever…" },
                                    { key: "message", label: "Anything else we should know?", long: true },
                                ]} />
                        )}
                    </PathwayCard>

                    <PathwayCard testid="pathway-host" kicker="run the thing" title="HOST AN EVENT" rotate="rotate-[0.5deg]"
                        body={t("involved.host_body", "If you can gather five people around a table, we'll help you do it. Tell us the idea, the timing, the crowd you expect, and what you'd need from the room. Approved events get pinned straight to the clubhouse calendar.")}>
                        <button onClick={() => toggle("host")} aria-expanded={openForm === "host"} data-testid="host-open-button" className="btn-stamp mt-5 !bg-brick">
                            Pitch Your Event
                        </button>
                        {openForm === "host" && (
                            <InquiryForm kind="host" title="PITCH THE THING" testid="host"
                                fields={[
                                    { key: "title", label: "What is it?", placeholder: "Zine night, chess club, puppet slam…" },
                                    { key: "timing", label: "When are you thinking?", placeholder: "Friday evenings, once a month, Nov 22…" },
                                    { key: "attendance", label: "Expected crowd", placeholder: "5 people, 25 people, unknown horde…" },
                                    { key: "equipment", label: "What do you need from the room?", placeholder: "tables, chairs, outlets, projector…" },
                                    { key: "details", label: "Anything else?", long: true },
                                ]} />
                        )}
                    </PathwayCard>

                    <PathwayCard testid="pathway-residency" kicker="move in" title="RESIDENT PARTNERSHIPS" rotate="rotate-[0.5deg]"
                        body={t("involved.residency_body", "The Clubhouse keeps a couple of rooms for small operations that belong here — like Rootbound Kava, Grimsy Book Nook, and Avery Howard's Mirror Room. Right now we're hunting a daytime café operator whose offerings complement the kava bar and the community programming.")}>
                        <button onClick={() => toggle("residency")} aria-expanded={openForm === "residency"} data-testid="residency-open-button" className="btn-stamp mt-5 !bg-forest">
                            Apply for the Café Spot
                        </button>
                        {openForm === "residency" && (
                            <InquiryForm kind="residency" title="TELL US ABOUT YOUR OPERATION" testid="residency"
                                fields={[
                                    { key: "title", label: "Your business or concept", placeholder: "daytime café, bakery cart…" },
                                    { key: "details", label: "What would you serve, and why the Clubhouse?", long: true },
                                ]} />
                        )}
                    </PathwayCard>

                    <PathwayCard testid="pathway-collaborate" kicker="stronger together" title="COMMUNITY COLLABORATIONS" rotate="-rotate-[0.5deg]"
                        body={t("involved.collab_body", "Organizations, educators, homeschool groups, artists, and neighborhood crews: if you're doing something good and need a room, an audience, or a partner in crime, let's scheme.")}>
                        <Link to="/contact" data-testid="collab-cta" className="btn-stamp mt-5 inline-block !bg-mustard !text-ink">
                            Start the Conversation →
                        </Link>
                    </PathwayCard>
                </div>

                <FadeUp className="mx-auto mt-10 max-w-6xl">
                    <div data-testid="pathway-contribute" className="flex flex-col items-start gap-5 border-2 border-dashed border-ink/40 bg-cream/50 p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9">
                        <div>
                            <h2 className="font-display text-2xl tracking-wide text-ink sm:text-3xl">JUST WANT TO CHIP IN?</h2>
                            <p className="mt-2 max-w-xl text-ink/75">
                                {t("involved.donate_body", "Every dollar becomes chairs, paint, and glue sticks. Donations are received by Panther City Clubhouse LLC and aren't tax-deductible — just deeply appreciated.")}
                            </p>
                        </div>
                        <Link to="/#donate" data-testid="contribute-cta" className="btn-stamp !bg-brick shrink-0">
                            Make a Donation
                        </Link>
                    </div>
                </FadeUp>

                <FadeUp className="mx-auto mt-14 max-w-6xl">
                    <div className="border-2 border-ink bg-forest p-8 hard-shadow-lg rotate-[0.25deg] sm:p-10">
                        <h2 className="font-display text-3xl tracking-wide text-cream">THINGS WE'D LOVE TO SEE</h2>
                        <ul className="mt-6 flex flex-wrap gap-3">
                            {ideas.map((idea) => (
                                <li key={idea} className="border-2 border-cream/40 bg-ink/20 px-3 py-1.5 font-display text-sm tracking-[0.12em] text-cream transition-transform duration-200 hover:-rotate-2 hover:bg-mustard hover:text-ink">
                                    {idea}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-8 font-hand text-2xl text-mustard -rotate-1">not on the list? even better.</p>
                    </div>
                </FadeUp>
            </section>

            <Marquee />
            <NewsletterSection idPrefix="involved-newsletter" />
        </>
    );
};

export default GetInvolved;
